import {Component, inject, Input, OnChanges, OnDestroy, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {NgClass} from '@angular/common';
import {
  BicButtonComponent,
  BicDropdownElementComponent,
  BicInputComponent,
  BicSelectComponent,
  BicTextareaComponent
} from '@buildinginformationcloud/bic-ui-components';
import {
  ChatService,
  ChatSurface,
  FeatureRequestBlockRef,
  UiBlock,
  UiBlockFeatureProposal,
  UiBlockOption,
  UiBlockSummaryField
} from '../service/chat.service';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';
import {
  faCircleExclamation,
  faClipboardList,
  faFile,
  faPlay,
  faScrewdriverWrench
} from '@fortawesome/free-solid-svg-icons';
import {NotificationsService} from '../../../services/notifications/notifications.service';
import {MarkdownComponent} from 'ngx-markdown';
import {normalizeMarkdown} from '../../../pipes/normalize-markdown.pipe';
import {summaryHeaderTitle} from '../util/message-copy-text';

@Component({
  selector: 'app-ui-block-panel',
  standalone: true,
  templateUrl: './ui-block-panel.component.html',
  styles: [`
    :host ::ng-deep .summary-row-icon svg {
      vertical-align: 0;
    }
  `],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    BicButtonComponent,
    BicDropdownElementComponent,
    BicInputComponent,
    BicSelectComponent,
    BicTextareaComponent,
    FaIconComponent,
    MarkdownComponent,
  ],
})
export class UiBlockPanelComponent implements OnChanges, OnDestroy {
  @Input() uiBlock?: UiBlock;
  @Input() compact = false;
  @Input() blockIndex = 0;
  @Input() messageId?: string;
  /** Chatfläche, zu der dieser Block gehört – Auswahl und Eingabe gehören genau dorthin. */
  @Input() surface: ChatSurface = 'classic';

  protected chatService = inject(ChatService);

  /** Der Zustand der Fläche, zu der dieser Block gehört. */
  get session() {
    return this.chatService.session(this.surface);
  }
  protected isUnavailableFeatureRequestSubmitting = false;
  /** Bestätigungsansicht der Feature-Anfrage ist aufgeklappt. */
  protected isFeatureRequestOpen = false;
  /** Die Anfrage ist raus – Button und Formular bleiben gesperrt, damit sie nicht doppelt geht. */
  protected isFeatureRequestSent = false;
  /** Bearbeitbare Kopie des Vorschlags; startet beim Aufklappen mit den Werten des Modells. */
  protected featureDraft: UiBlockFeatureProposal = {title: '', problem: '', desiredBehavior: '', area: ''};
  /** chatBIC erzeugt gerade einen Vorschlag (Block ohne `featureProposal`); Eingaben und Senden sind gesperrt. */
  protected isFeatureProposalLoading = false;
  /** Der Vorschlag konnte nicht erzeugt werden – das Formular bleibt leer, der Nutzer schreibt selbst. */
  protected featureProposalFailed = false;
  /** Checkbox „Chatverlauf mitsenden"; bei jedem Öffnen wieder an. */
  protected includeChatHistory = true;
  private featureProposalSubscription?: Subscription;
  /** Bereiche, die das Backend kennt. */
  protected readonly featureAreas = [
    'myCheck', 'Prüfläufe', 'Prüfberichte', 'Dateien und Dokumente', 'IFC-Viewer', 'Aufgaben',
    'Projekteinstellungen', 'chatBIC', 'Sonstiges',
  ];
  private featureBlockKey = '';
  protected selectedValues = new Set<string>();
  private notificationsService = inject(NotificationsService);
  private readonly blockedOptionPatterns: RegExp[] = [
    /add\s*(more|another|additional)/i,
    /\bmehr\b/i,
    /weitere?\s+hinzu(fügen|fuegen)/i,
  ];
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faScrewdriverWrench = faScrewdriverWrench;

  get hasOptions(): boolean {
    return this.filteredOptions.length > 0;
  }

  get filteredOptions(): UiBlockOption[] {
    return (this.uiBlock?.options ?? []).filter(option => !this.isBlockedOption(option));
  }

  get isOptionsType(): boolean {
    return this.uiBlock?.type === 'options';
  }

  get isSummaryType(): boolean {
    return this.uiBlock?.type === 'summary';
  }

  get isUnavailableType(): boolean {
    return this.uiBlock?.type === 'unavailable';
  }

  get shouldShowUnavailableFeatureRequest(): boolean {
    return this.uiBlock?.shouldImplement === true;
  }

  get isSingleUploadAction(): boolean {
    const options = this.filteredOptions;
    return !this.uiBlock?.multiSelect && options.length === 1 && options[0].value === 'upload_file';
  }

  get summaryFields(): UiBlockSummaryField[] {
    return this.uiBlock?.summaryFields ?? [];
  }

  /** Freitext-Zusammenfassung – das Modell schreibt sie wie den Antworttext als Markdown. */
  get summaryMarkdown(): string {
    return normalizeMarkdown(this.uiBlock?.summary ?? '');
  }

  get summaryHeaderTitle(): string {
    return summaryHeaderTitle(this.uiBlock);
  }

  isSelected(option: UiBlockOption): boolean {
    return this.selectedValues.has(option.value);
  }

  onOptionClick(option: UiBlockOption): void {
    if (!this.uiBlock?.multiSelect) {
      this.chatService.registerUiBlockSelection(this.surface, [option.value], this.blockIndex, this.uiBlock?.type ?? 'options', this.messageId);
      if (option.value === 'upload_file') {
        this.chatService.openContextSelector(this.surface);
        return;
      }
      this.selectedValues.clear();
      this.selectedValues.add(option.value);
      this.session.question = option.label;
      return;
    }

    if (this.selectedValues.has(option.value)) {
      this.selectedValues.delete(option.value);
    } else {
      this.selectedValues.add(option.value);
    }

    const selectedLabels = this.filteredOptions
      .filter(currentOption => this.selectedValues.has(currentOption.value))
      .map(currentOption => currentOption.label);
    const selectedValues = this.filteredOptions
      .filter(currentOption => this.selectedValues.has(currentOption.value))
      .map(currentOption => currentOption.value);
    if (selectedValues.length > 0) {
      this.chatService.registerUiBlockSelection(this.surface, selectedValues, this.blockIndex, this.uiBlock?.type ?? 'options', this.messageId);
    } else {
      this.chatService.clearPendingUiBlockSelection(this.surface);
    }
    this.session.question = selectedLabels.join(', ');
  }

  trackByValue(_: number, option: UiBlockOption): string {
    return option.value;
  }

  trackBySummaryField(index: number, field: UiBlockSummaryField): string {
    return `${field.title}-${index}`;
  }

  trackByBulletPoint(index: number, bulletPoint: string): string {
    return `${bulletPoint}-${index}`;
  }

  getSummaryFieldIcon(title: string): IconDefinition {
    const normalizedTitle = this.normalizeSummaryTitle(title);

    if (normalizedTitle.includes('allgemeine angaben')) {
      return faPlay;
    }
    if (
      normalizedTitle.includes('pruefregeln')
      || normalizedTitle.includes('prufregeln')
      || normalizedTitle.includes('dateien')
    ) {
      return faClipboardList;
    }
    if (normalizedTitle.includes('bericht')) {
      return faFile;
    }

    return faPlay;
  }

  private get blockRef(): FeatureRequestBlockRef {
    return {messageId: this.messageId, blockIndex: this.blockIndex};
  }

  private featureAreaOptionsArea?: string;
  private cachedFeatureAreaOptions: string[] = this.featureAreas;

  get featureProposal(): UiBlockFeatureProposal | undefined {
    const proposal: unknown = this.uiBlock?.featureProposal;
    if (!proposal || typeof proposal !== 'object') return undefined;
    const candidate = proposal as Partial<Record<keyof UiBlockFeatureProposal, unknown>>;
    // Ein unvollständiges oder falsch typisiertes Modell-Ergebnis zählt nicht: dann erzeugt chatBIC einen neuen Vorschlag.
    return typeof candidate.title === 'string'
    && typeof candidate.problem === 'string'
    && typeof candidate.desiredBehavior === 'string'
    && typeof candidate.area === 'string'
      ? candidate as UiBlockFeatureProposal
      : undefined;
  }

  /** Bereiche zur Auswahl; ein vom Modell gelieferter, unbekannter Bereich bleibt wählbar. */
  get featureAreaOptions(): string[] {
    const area = this.featureDraft.area;
    if (area !== this.featureAreaOptionsArea) {
      this.featureAreaOptionsArea = area;
      this.cachedFeatureAreaOptions = !area || this.featureAreas.includes(area)
        ? this.featureAreas
        : [...this.featureAreas, area];
    }
    return this.cachedFeatureAreaOptions;
  }

  /** Die Checkbox zum Chatverlauf gilt nur, wenn die Anfrage einen Thread mitschickt. */
  get sendsChatHistory(): boolean {
    return !!this.chatService.featureRequestThreadId(this.surface);
  }

  /** Senden ist erst möglich, wenn Titel, Problem und Bereich gesetzt sind. */
  get canSendFeatureDraft(): boolean {
    const draft = this.featureDraft;
    return !this.isFeatureProposalLoading && !!draft.title.trim() && !!draft.problem.trim() && !!draft.area.trim();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['uiBlock']) {
      this.selectedValues.clear();
      // Die Feature-Anfrage gehört zu einem Block, nicht zum Objekt: wird die Nachricht nur neu aufgebaut
      // (Stream-Ende, Neuladen), bleiben offenes Formular, Eingaben und „Angefragt" stehen.
      const blockKey = JSON.stringify(this.uiBlock ?? null);
      if (blockKey !== this.featureBlockKey) {
        this.featureBlockKey = blockKey;
        this.isFeatureRequestOpen = false;
        this.stopFeatureProposal();
      }
      // „Angefragt" liegt im Service, damit ein neu aufgebautes Panel die Anfrage nicht erneut freigibt.
      this.isFeatureRequestSent = this.chatService.hasSentFeatureRequest(this.surface, this.uiBlock, this.blockRef);
    }
  }

  ngOnDestroy(): void {
    this.stopFeatureProposal();
  }

  /**
   * „Feature anfragen": öffnet immer erst das Formular, gesendet wird nur nach „Senden". Liefert der Block
   * einen Vorschlag des Modells, ist das Formular damit vorbefüllt. Sonst lässt chatBIC einen Vorschlag
   * aus dem Chat erzeugen; scheitert das oder gibt es keinen serverseitigen Thread, bleibt es leer.
   */
  onUnavailableFeatureRequestClick(): void {
    if (this.isUnavailableFeatureRequestSubmitting || this.isFeatureRequestSent) return;
    this.stopFeatureProposal();
    this.includeChatHistory = true;
    this.featureProposalFailed = false;
    const proposal = this.featureProposal;
    this.featureDraft = {
      title: proposal?.title ?? '',
      problem: proposal?.problem ?? '',
      desiredBehavior: proposal?.desiredBehavior ?? '',
      area: proposal?.area ?? '',
    };
    this.isFeatureRequestOpen = true;
    if (!proposal && this.chatService.featureRequestThreadId(this.surface)) {
      this.loadFeatureProposal();
    }
  }

  onFeatureRequestConfirm(): void {
    if (!this.canSendFeatureDraft) return;
    this.submitFeatureRequest(this.featureDraft);
  }

  onFeatureRequestCancel(): void {
    if (this.isUnavailableFeatureRequestSubmitting) return;
    this.stopFeatureProposal();
    this.isFeatureRequestOpen = false;
  }

  private loadFeatureProposal(): void {
    this.isFeatureProposalLoading = true;
    this.featureProposalSubscription = this.chatService.requestFeatureProposal(this.surface, this.uiBlock).pipe(
      finalize(() => {
        this.isFeatureProposalLoading = false;
      })
    ).subscribe({
      next: proposal => {
        this.featureDraft = {...proposal};
      },
      error: (error: unknown) => {
        this.featureProposalFailed = true;
        console.warn('Feature-Vorschlag konnte nicht erzeugt werden:', error);
      }
    });
  }

  private stopFeatureProposal(): void {
    this.featureProposalSubscription?.unsubscribe();
    this.featureProposalSubscription = undefined;
    this.isFeatureProposalLoading = false;
  }

  private submitFeatureRequest(proposal: UiBlockFeatureProposal): void {
    if (this.isUnavailableFeatureRequestSubmitting || this.isFeatureRequestSent) return;
    this.isUnavailableFeatureRequestSubmitting = true;

    this.chatService.submitUnavailableFeatureRequest(this.surface, this.uiBlock, proposal, this.includeChatHistory, this.blockRef).pipe(
      finalize(() => {
        this.isUnavailableFeatureRequestSubmitting = false;
      })
    ).subscribe({
      next: () => {
        this.isFeatureRequestSent = true;
        this.isFeatureRequestOpen = false;
        this.notificationsService.showSuccessMessage('Feature-Anfrage wurde gesendet.');
      },
      error: (error: unknown) => {
        console.error('Error submitting unavailable feature request:', error);
        this.notificationsService.showErrorMessage('Feature-Anfrage konnte nicht gesendet werden.');
      }
    });
  }

  private isBlockedOption(option: UiBlockOption): boolean {
    const label = option.label.trim();
    const value = option.value.trim();
    return this.blockedOptionPatterns.some(pattern => pattern.test(label) || pattern.test(value));
  }

  private normalizeSummaryTitle(title: string): string {
    return title
      .trim()
      .toLocaleLowerCase('de-DE')
      .replace(/ü/g, 'ue')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }
}
